import { computed, type ComputedRef } from 'vue'

import type {
  ChartAvailabilityInput,
  ChartTypeMeta,
  VisualizationTypeDefinition,
} from '../models/chart.types'
import {
  fallbackChartTypeCatalog,
  getAvailableChartTypes,
  resolveChartTypeCatalog,
} from '../service/chart/visualizationCatalog'
import {
  useVisualizationTypeCatalogStore,
  type VisualizationTypeCatalogStatus,
} from '../stores/visualizationTypeCatalogStore'

export interface UseVisualizationTypeCatalog {
  status: ComputedRef<VisualizationTypeCatalogStatus>
  settled: ComputedRef<boolean>
  definitions: ComputedRef<VisualizationTypeDefinition[]>
  chartTypes: ComputedRef<ChartTypeMeta[]>
  availableChartTypes: (input: ChartAvailabilityInput) => ChartTypeMeta[]
}

/** Backend metadata reader with a local, render-truthful degraded catalog. An unsettled boot
 *  deliberately exposes no capability, including when a warm cache exists: DataVisible holds
 *  only its chart surface in loading until this boot's manifest/fallback decision completes. */
export function useVisualizationTypeCatalog(): UseVisualizationTypeCatalog {
  const store = useVisualizationTypeCatalogStore()
  const backendChartTypes = computed(() => resolveChartTypeCatalog(store.definitions))
  const funnelFallback = fallbackChartTypeCatalog.find(({ type }) => type === 'funnel')
  const chartTypes = computed<ChartTypeMeta[]>(() => {
    if (!store.settled) return []
    if (store.status !== 'ready') return [...fallbackChartTypeCatalog]
    // Funnel is a frontend-owned renderer for Insights' sibling-table payload. It must remain
    // usable in DataVisible even while the backend visualization catalog has no funnel entry.
    return backendChartTypes.value.some(({ type }) => type === 'funnel') || !funnelFallback
      ? backendChartTypes.value
      : [...backendChartTypes.value, funnelFallback]
  })

  return {
    status: computed(() => store.status),
    settled: computed(() => store.settled),
    definitions: computed(() => store.definitions),
    chartTypes,
    availableChartTypes: (input) => getAvailableChartTypes(chartTypes.value, input),
  }
}
